import React, { useState, useMemo, useEffect, useRef, useCallback, useDeferredValue } from 'react';
import { useAuth } from '../auth/useAuth';
import { fetchProducts, fetchAllProducts, insertIssue, fetchSpotProducts } from '../services/dataService';
import { useSuggestions } from '../hooks/useSuggestions';
import { expandSearchTerms, matchesAllGroups, rankProduct } from '../utils/searchUtils';
import { validateSpotLink, SPOT_LINK_FORMAT_HINT, buildSpotUrl } from '../utils/spotLink';
import ResizableSortableTableHeader from '../components/ResizableSortableTableHeader';
import ProductSpotHistoryModal from '../components/productHistory/ProductSpotHistoryModal';
import {
  Search,
  Eye,
  X,
  Upload,
  Download,
  Package,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FilterX,
  MessageSquare,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Check,
  ChevronDown,
  Save,
  HelpCircle,
  History
} from 'lucide-react';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';
// Catalog extras (Relevance / Status) are not present in the RDS table — disabled for now
const SHOW_CATALOG_EXTRAS = false;
const PAGE_SIZE = 50;

const ProductsManagerLoadingState = ({ loadingStatus, loadedCount, totalCount }) => {
  const skeletonRows = Array.from({ length: 10 }, (_, index) => index);
  const hasProgress = Number.isFinite(totalCount) && totalCount > 0;
  const progressPercent = hasProgress ? Math.round((loadedCount / totalCount) * 100) : null;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm" aria-live="polite">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-cyan-50" />

      <div className="relative p-8 md:p-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">Products Manager</p>
            <h2 className="mt-2 text-2xl font-bold text-neutral-800">Loading Product Catalog</h2>
            <p className="mt-1 text-sm text-neutral-500">{loadingStatus}</p>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white/90 px-4 py-3 shadow-sm min-w-[220px]">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Progress</p>
            <p className="mt-1 text-lg font-bold text-neutral-800">
              {loadedCount.toLocaleString()} {hasProgress ? `/ ${totalCount.toLocaleString()}` : ''}
            </p>
            {hasProgress && (
              <div className="mt-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
                  <div
                    className="h-full rounded-full bg-primary-600 transition-all duration-500"
                    style={{ width: `${Math.min(100, progressPercent)}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-neutral-500">{progressPercent}% ready</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <div className="grid grid-cols-14 gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3">
            <div className="col-span-2 h-4 rounded bg-neutral-200/80 pm-skeleton-shimmer" />
            <div className="col-span-4 h-4 rounded bg-neutral-200/80 pm-skeleton-shimmer" />
            <div className="col-span-2 h-4 rounded bg-neutral-200/80 pm-skeleton-shimmer" />
            <div className="col-span-2 h-4 rounded bg-neutral-200/80 pm-skeleton-shimmer" />
            <div className="col-span-2 h-4 rounded bg-neutral-200/80 pm-skeleton-shimmer" />
            <div className="col-span-2 h-4 rounded bg-neutral-200/80 pm-skeleton-shimmer" />
          </div>

          <div className="divide-y divide-neutral-100">
            {skeletonRows.map((row) => (
              <div key={row} className="grid grid-cols-14 gap-3 px-4 py-3">
                <div className="col-span-2 h-4 rounded bg-neutral-100 pm-skeleton-shimmer" />
                <div className="col-span-4 h-4 rounded bg-neutral-100 pm-skeleton-shimmer" />
                <div className="col-span-2 h-4 rounded bg-neutral-100 pm-skeleton-shimmer" />
                <div className="col-span-2 h-4 rounded bg-neutral-100 pm-skeleton-shimmer" />
                <div className="col-span-2 h-4 rounded bg-neutral-100 pm-skeleton-shimmer" />
                <div className="col-span-2 h-4 rounded bg-neutral-100 pm-skeleton-shimmer" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const { user, isAuthenticated, isLoading: authLoading } = BYPASS_AUTH ? {} : useAuth();
  
  // --- STATE ---
  const [products, setProducts] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterBrand, setFilterBrand] = useState([]);
  const [filterSize, setFilterSize] = useState([]);
  const [filterRetailer, setFilterRetailer] = useState('All Retailers');
  // Product whose spot history modal is open (null = closed)
  const [historyProduct, setHistoryProduct] = useState(null);
  // UPCs copied from inside the history modal. Kept separate from
  // disabledProducts: they stay marked while the modal is open and reset only
  // when it's reopened from "View Product History" (or one by one via the
  // double-click re-enable confirmation).
  const [historyUsedUpcs, setHistoryUsedUpcs] = useState(() => new Set());
  const [brandSearchInput, setBrandSearchInput] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [selectedBrandIndex, setSelectedBrandIndex] = useState(-1);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [tempFilterBrand, setTempFilterBrand] = useState([]);
  const [tempFilterCategory, setTempFilterCategory] = useState([]);
  const [tempFilterSize, setTempFilterSize] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewAbortRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [clipboardToast, setClipboardToast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);  // Start true to show spinner on initial load
  const [loadingStatus, setLoadingStatus] = useState('Connecting to product catalog...');
  const [currentApiSearch, setCurrentApiSearch] = useState('');
  const [catalogStats, setCatalogStats] = useState({ loaded: 0, total: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [commentModalProduct, setCommentModalProduct] = useState(null);
  const [productComment, setProductComment] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [showSpotClipboardModal, setShowSpotClipboardModal] = useState(false);
  const [spotClipboardText, setSpotClipboardText] = useState('');
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({});
  const [newProducts, setNewProducts] = useState([]); // Separate state for created products
  const [isSavingNewProduct, setIsSavingNewProduct] = useState(false);
  const [newProductError, setNewProductError] = useState(null);
  // Field-level error for Spot Link, kept apart from newProductError so a
  // format complaint sits under the field it belongs to rather than in the
  // form-wide banner.
  const [spotLinkError, setSpotLinkError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [spotSearchInput, setSpotSearchInput] = useState('');
  const [columnWidths, setColumnWidths] = useState({
    actions: 120,
    description: 300,
    category: 150,
    size: 150,
    form: 120,
    uom: 120,
    relevance: 120,
    status: 100
  });
  const [searchMode, setSearchMode] = useState('advanced'); // 'simple' or 'advanced'
  const [filterByUpc, setFilterByUpc] = useState([]); // UPC list to filter by
  const [isUpcSearchMode, setIsUpcSearchMode] = useState(false); // Flag to hide search badge during UPC filtering
  const [disabledProducts, setDisabledProducts] = useState(new Set()); // Set of disabled product UPCs
  const [confirmReEnableProduct, setConfirmReEnableProduct] = useState(null); // Product to re-enable

  // Full catalog preload: one gzipped request fetches all rows once per session.
  // While null, loadPage falls back to the paginated API for graceful degradation.
  const [fullCatalog, setFullCatalog] = useState(null);
  const [preloadDone, setPreloadDone] = useState(false);
  const fullCatalogRef = useRef(null);
  useEffect(() => { fullCatalogRef.current = fullCatalog; }, [fullCatalog]);

  // --- PRELOAD: fetch the full catalog once per session ---
  // Falls back silently to the paginated API if this errors; loadPage handles both.
  useEffect(() => {
    if (!BYPASS_AUTH && authLoading) return;
    if (!BYPASS_AUTH && !isAuthenticated) return;
    if (fullCatalog) return;

    let cancelled = false;
    setLoadingStatus('Loading full product catalog...');
    const t0 = performance.now();
    (async () => {
      const result = await fetchAllProducts();
      if (cancelled) return;
      if (!result) {
        console.warn('[ProductsManager] Full-catalog preload failed; falling back to paginated API.');
        setPreloadDone(true);
        return;
      }
      const ms = Math.round(performance.now() - t0);
      // Pre-sort the catalog by id DESC so the no-search page-1 view matches what the
      // server used to return (ORDER BY id DESC). Without this, slice(0, 50) over the
      // raw ASC payload puts the lowest-id rows on screen — which for this dataset is
      // dominated by a handful of low-id Motrin SKUs and surfaces unfamiliar brands
      // in the filter modal's facet lists.
      const sorted = [...result.data].sort((a, b) => {
        const aId = Number(a.id);
        const bId = Number(b.id);
        if (Number.isFinite(aId) && Number.isFinite(bId)) return bId - aId;
        return String(b.id || '').localeCompare(String(a.id || ''), undefined, { numeric: true });
      });
      console.info(`[ProductsManager] Preloaded ${sorted.length} products in ${ms}ms`);
      setFullCatalog(sorted);
      setPreloadDone(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  // Helper: apply searchTerm + filters to the cached catalog (client-side).
  // Mirrors the server's buildSearchClause + rankProduct in lambda/routes/products.js
  // so that semantics (AND across words, OR within translation group, then rank-then-id
  // ordering) match what the page returned before the preload swap.
  const filterCatalogClientSide = (catalog, search, filters = {}) => {
    const termGroups = expandSearchTerms(search || '');
    const hasSearch = termGroups.length > 0;

    let rows = catalog;
    if (hasSearch) {
      rows = rows.filter((p) => matchesAllGroups(p, termGroups));
    }
    if (filters.brand) rows = rows.filter((p) => p.brand === filters.brand);
    if (filters.category) rows = rows.filter((p) => p.category === filters.category);
    if (filters.size) {
      rows = rows.filter((p) => [p.form, p.size, p.uom].filter(Boolean).join(' ') === filters.size);
    }

    if (!hasSearch) {
      // No search: leave catalog ordered by id ASC; downstream default sort flips to id DESC
      // (same as the original server's no-search ORDER BY id DESC).
      return rows;
    }

    // Stamp _rank and pre-sort the FULL filtered set so pagination shows top-ranked first.
    // Downstream sortedProducts picks up _rank when no explicit user sort is chosen.
    const ranked = rows.map((row) => ({ ...row, _rank: rankProduct(row, termGroups) }));
    ranked.sort((a, b) => {
      if (a._rank !== b._rank) return a._rank - b._rank;
      const aId = Number(a.id);
      const bId = Number(b.id);
      if (Number.isFinite(aId) && Number.isFinite(bId)) return bId - aId;
      return String(b.id || '').localeCompare(String(a.id || ''), undefined, { numeric: true });
    });
    return ranked;
  };

  // --- SERVER-SIDE SUGGESTIONS (for modal filters) ---
  // These replace the client-side useMemo filters to access the full catalog
  // useSuggestions stays as a fallback for when the preload hasn't completed
  // (slow connection, preload error). When fullCatalog is loaded we shadow these
  // values below with catalog-derived lists that are NOT capped at 50.
  const {
    suggestions: _serverBrandsRaw,
    isLoading: brandsLoading,
    isTyping: brandsTyping,
    error: brandsError,
    refetch: refetchBrands,
  } = useSuggestions({
    type: 'brand',
    query: brandSearchInput,
    enabled: showFilterModal && modalStep === 1 && !fullCatalog,
    limit: 50,
  });

  const {
    suggestions: _serverCategoriesRaw,
    isLoading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useSuggestions({
    type: 'category',
    brand: tempFilterBrand[0] || '',
    enabled: showFilterModal && modalStep === 2 && !fullCatalog,
    limit: 50,
  });

  const {
    suggestions: _serverSizesRaw,
    isLoading: sizesLoading,
    error: sizesError,
    refetch: refetchSizes,
  } = useSuggestions({
    type: 'size',
    brand: tempFilterBrand[0] || '',
    category: tempFilterCategory[0] || '',
    enabled: showFilterModal && modalStep === 3 && !fullCatalog,
    limit: 50,
  });

  // Catalog-derived suggestion lists (no 50-cap). When the preload is available,
  // these replace the server suggestions in the advanced-search modal so brands
  // like Econo (1k+ SKUs across many categories) show every category, not just
  // the alphabetic first 50.
  //
  // Caps below keep the rendered <button> list under control: with ~9.2k distinct
  // brands and no virtualization, rendering the full list on every keystroke
  // dropped characters in the input. The cap is well above any practical typing
  // session and naturally shrinks as the user adds more prefix characters.
  const SUGGESTION_RENDER_CAP = 200;

  // Compute distinct brand set once per catalog load — recomputing it per keystroke
  // forced a full 63k-row reduce on every character typed.
  const allBrandsFromCatalog = useMemo(() => {
    if (!fullCatalog) return null;
    const set = new Set();
    for (const p of fullCatalog) if (p.brand) set.add(p.brand);
    return [...set].sort((a, b) => String(a).localeCompare(String(b)));
  }, [fullCatalog]);

  // Decouple the input value from the (potentially heavy) list re-render. The input
  // updates synchronously on every keystroke so no character is ever dropped; the
  // filtered list catches up on the next idle frame. Net effect: typing "Unbranded
  // pica" feels native even though each keystroke triggers a fresh filter pass.
  const deferredBrandSearchInput = useDeferredValue(brandSearchInput);

  const serverBrands = useMemo(() => {
    if (!allBrandsFromCatalog) return _serverBrandsRaw || [];
    const q = (deferredBrandSearchInput || '').trim().toLowerCase();
    const filtered = q
      ? allBrandsFromCatalog.filter((b) => String(b).toLowerCase().startsWith(q))
      : allBrandsFromCatalog;
    return filtered.slice(0, SUGGESTION_RENDER_CAP);
  }, [allBrandsFromCatalog, deferredBrandSearchInput, _serverBrandsRaw]);

  const serverCategories = useMemo(() => {
    if (!fullCatalog) return _serverCategoriesRaw || [];
    const brand = tempFilterBrand[0];
    const pool = brand ? fullCatalog.filter((p) => p.brand === brand) : fullCatalog;
    const cats = [...new Set(pool.map((p) => p.category).filter(Boolean))];
    cats.sort((a, b) => String(a).localeCompare(String(b)));
    return cats.slice(0, SUGGESTION_RENDER_CAP);
  }, [fullCatalog, tempFilterBrand, _serverCategoriesRaw]);

  const serverSizes = useMemo(() => {
    if (!fullCatalog) return _serverSizesRaw || [];
    const brand = tempFilterBrand[0];
    const category = tempFilterCategory[0];
    let pool = fullCatalog;
    if (brand) pool = pool.filter((p) => p.brand === brand);
    if (category) pool = pool.filter((p) => p.category === category);
    const sizes = [...new Set(
      pool.map((p) => [p.form, p.size, p.uom].filter(Boolean).join(' ')).filter(Boolean)
    )];
    sizes.sort((a, b) => String(a).localeCompare(String(b)));
    return sizes.slice(0, SUGGESTION_RENDER_CAP);
  }, [fullCatalog, tempFilterBrand, tempFilterCategory, _serverSizesRaw]);

  // Fetch preview count from API when modal filters change
  useEffect(() => {
    if (!showFilterModal) {
      setPreviewCount(null);
      return;
    }

    // Cancel any pending request
    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }

    const hasFilters = tempFilterBrand.length > 0 || tempFilterCategory.length > 0 || tempFilterSize.length > 0;

    if (!hasFilters) {
      // No filters selected - show total catalog count
      setPreviewCount(catalogStats.total);
      return;
    }

    // Fetch filtered count from API
    const fetchPreviewCount = async () => {
      previewAbortRef.current = new AbortController();
      setPreviewLoading(true);

      try {
        const result = await fetchProducts({
          limit: 1,
          offset: 0,
          brand: tempFilterBrand[0] || null,
          category: tempFilterCategory[0] || null,
          size: tempFilterSize[0] || null,
        });

        if (result && result.pagination) {
          setPreviewCount(result.pagination.total);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[ProductsManager] Preview count error:', err);
        }
      } finally {
        setPreviewLoading(false);
      }
    };

    // Small debounce to avoid too many requests
    const timer = setTimeout(fetchPreviewCount, 150);
    return () => {
      clearTimeout(timer);
      if (previewAbortRef.current) {
        previewAbortRef.current.abort();
      }
    };
  }, [showFilterModal, tempFilterBrand, tempFilterCategory, tempFilterSize, catalogStats.total]);

  const extractedSpotUpcs = useMemo(() => {
    // This is kept for backward compatibility, but the new workflow uses handleAutoPasteSpot
    if (!spotClipboardText.trim()) return [];

    const marker = 'Product(s) in this spot:';
    const markerIndex = spotClipboardText.indexOf(marker);
    if (markerIndex === -1) return [];

    const sectionText = spotClipboardText.slice(markerIndex + marker.length);
    const regex = /upc:\s*([^\]]+)\]/gi;
    const upcs = [];
    let match;

    while ((match = regex.exec(sectionText)) !== null) {
      const cleanedUpc = (match[1] || '').trim();
      if (cleanedUpc) upcs.push(cleanedUpc);
    }

    return [...new Set(upcs)];
  }, [spotClipboardText]);

  // --- API: Load first product batch, then hydrate catalog in the background ---
  // --- API: Load one page of products from server ---
  const loadPage = async (page, searchQuery = '', filters = {}) => {
    // Fast path: full catalog is preloaded — filter + paginate in memory, no network.
    const catalog = fullCatalogRef.current;
    if (catalog) {
      setApiError(false);
      const rows = filterCatalogClientSide(catalog, searchQuery, filters);
      const offset = (page - 1) * PAGE_SIZE;
      setProducts(rows.slice(offset, offset + PAGE_SIZE));
      setCatalogStats({ loaded: rows.length, total: rows.length });
      setCurrentPage(page);
      setCurrentApiSearch(searchQuery);
      setIsLoading(false);
      return;
    }

    // Fallback path: catalog not yet loaded (preload failed or in flight) — hit the API.
    setIsLoading(true);
    setApiError(false);
    setLoadingStatus('Loading products...');
    const offset = (page - 1) * PAGE_SIZE;
    try {
      const result = await fetchProducts({
        limit: PAGE_SIZE,
        offset,
        search: searchQuery || null,
        brand: filters.brand || null,
        category: filters.category || null,
        size: filters.size || null,
      });
      if (result === null) {
        setApiError(true);
        return;
      }
      setProducts(result.data || []);
      setCatalogStats({
        loaded: result.data?.length ?? 0,
        total: result.pagination?.total ?? 0,
      });
      setCurrentPage(page);
      setCurrentApiSearch(searchQuery);
    } catch (err) {
      console.error('[ProductsManager] API load error:', err);
      setApiError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Search for multiple UPCs server-side by making sequential requests
  // Strategy: Search one UPC at a time, showing results progressively as they arrive.
  // This provides immediate visual feedback to the user.
  const searchProductsByUpcs = async (upcs) => {
    setIsLoading(true);
    setApiError(false);

    // Fast path: full catalog is preloaded — match all UPCs in a single pass.
    const catalog = fullCatalogRef.current;
    if (catalog) {
      const wanted = new Set(upcs.map((u) => String(u).trim()).filter(Boolean));
      const matched = catalog.filter((p) => wanted.has(String(p.upc || '').trim()));
      setProducts(matched);
      setCatalogStats({ loaded: matched.length, total: matched.length });
      setCurrentPage(1);
      setCurrentApiSearch('');
      setLoadingStatus('Search complete!');
      setIsLoading(false);
      return;
    }

    try {
      const productMap = new Map(); // Deduplicate by UPC

      // Search one UPC at a time, updating UI progressively
      for (let i = 0; i < upcs.length; i++) {
        const upc = upcs[i];
        setLoadingStatus(`Searching for UPC ${i + 1} of ${upcs.length}...`);
        
        try {
          const result = await fetchProducts({ limit: 50, offset: 0, search: upc });
          
          if (result === null) {
            console.error(`Failed to fetch products for UPC: ${upc}`);
            continue;
          }
          
          // Add results to map (deduplicates automatically)
          (result.data || []).forEach(product => {
            productMap.set(product.upc, product);
          });
          
          // Progress tracks the number of spot products searched (1/10, 2/10…),
          // not the running match count.
          const currentProducts = Array.from(productMap.values());
          setProducts(currentProducts);
          setCatalogStats({
            loaded: i + 1,
            total: upcs.length,
          });
        } catch (error) {
          console.error(`Error fetching UPC ${upc}:`, error);
        }
      }

      // Final stats reflect the actual products found (used by the results count).
      const finalProducts = Array.from(productMap.values());
      setCatalogStats({
        loaded: finalProducts.length,
        total: finalProducts.length,
      });
      setCurrentPage(1);
      setCurrentApiSearch('');
      setLoadingStatus('Search complete!');
    } catch (err) {
      console.error('[ProductsManager] API search error:', err);
      setApiError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search input to prevent lag while typing (300ms for server-side calls)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // --- CSV PARSING ---
  const parseCSV = (text) => {
    const rows = text.split(/\r?\n/).filter(row => row.trim() !== "");
    if (rows.length === 0) return;
    
    // Detect delimiter: check if first row has tabs or commas
    // Count tabs and commas in the header row
    const headerRow = rows[0];
    const tabCount = (headerRow.match(/\t/g) || []).length;
    const commaCount = (headerRow.match(/,/g) || []).length;
    
    // Use tab if there are more tabs than commas, otherwise use comma
    const delimiter = tabCount > commaCount ? '\t' : ',';
    
    const headers = rows[0].split(delimiter).map(h => h.trim());
    
    const jsonData = rows.slice(1).map((row, index) => {
      // Better CSV parsing that handles quoted fields AND detects delimiter
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if ((char === delimiter) && !inQuotes) {
          values.push(current.trim().replace(/^"|"$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim().replace(/^"|"$/g, ''));
      
      const obj = { _originalIndex: index };
      headers.forEach((header, i) => {
        obj[header] = values[i] || '';
      });
      
      return obj;
    }).filter(item => {
      // Remove completely empty rows - check if at least one CSV field has a value
      // Exclude _originalIndex from the check
      return Object.entries(item)
        .filter(([key]) => key !== '_originalIndex')
        .some(([, val]) => String(val).trim() !== '');
    });
    
    console.log('💾 CSV Debug Info:');
    console.log('  Headers:', headers);
    console.log('  Total rows parsed after filtering:', jsonData.length);
    if (jsonData.length > 0) {
      console.log('  First row FIELDS:', Object.keys(jsonData[0]));
      console.log('  First row VALUES:', {
        id: jsonData[0]['id'],
        upc: jsonData[0]['upc'],
        brand: jsonData[0]['brand'],
        description: jsonData[0]['description'],
        category: jsonData[0]['category']
      });
      console.log('  Full first row:', jsonData[0]);
    }
    console.log('  All rows:', jsonData);
    
    setProducts(jsonData);
    setCatalogStats({ loaded: jsonData.length, total: jsonData.length });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Prevent concurrent uploads
    if (isUploading) {
      setLoadError('Please wait for the current upload to complete');
      return;
    }

    // Validate file size (20MB limit)
    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setLoadError('File too large. Maximum size is 20MB');
      return;
    }

    // Validate file type
    if (!file.name.match(/\.(csv|txt)$/i)) {
      setLoadError('Invalid file type. Please upload a CSV or TXT file');
      return;
    }

    setIsUploading(true);
    setLoadError(null);

    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        parseCSV(event.target.result);
        setLoadError(null);
      } catch (error) {
        console.error('CSV parsing error:', error);
        setLoadError(`Failed to parse CSV: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    };
    
    reader.onerror = (error) => {
      console.error('File read error:', error);
      setLoadError('Failed to read file. Please try again');
      setIsUploading(false);
    };
    
    reader.readAsText(file, 'UTF-8');
  };

  // --- SMART SEARCH HELPERS ---
  // Remove accents for better matching
  const removeAccents = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Spanish-English translation dictionary for common food terms
  const translations = {
    // Meats & Proteins
    'pollo': 'chicken',
    'chicken': 'pollo',
    'carne': 'meat beef',
    'meat': 'carne',
    'beef': 'res carne',
    'res': 'beef carne',
    'cerdo': 'pork',
    'pork': 'cerdo puerco',
    'puerco': 'pork cerdo',
    'pescado': 'fish',
    'fish': 'pescado',
    'salmon': 'salmon',
    'atun': 'tuna',
    'tuna': 'atun',
    'camarones': 'shrimp',
    'shrimp': 'camarones',
    'langosta': 'lobster',
    'lobster': 'langosta',
    'jamon': 'ham',
    'ham': 'jamon',
    'salchicha': 'sausage hotdog',
    'sausage': 'salchicha',
    'hotdog': 'salchicha perro',
    'tocino': 'bacon',
    'bacon': 'tocino',
    'chorizo': 'chorizo sausage',
    'pavo': 'turkey',
    'turkey': 'pavo',
    'huevo': 'egg',
    'egg': 'huevo',
    'huevos': 'eggs',
    'eggs': 'huevos',
    
    // Poultry Parts
    'alas': 'wings alitas',
    'alitas': 'wings alas',
    'wings': 'alas alitas',
    'pechuga': 'breast',
    'breast': 'pechuga',
    'muslo': 'thigh',
    'thigh': 'muslo',
    'pierna': 'leg',
    'leg': 'pierna',
    'pata': 'leg foot paw',
    'cadera': 'hip',
    
    // Dairy & Frozen
    'leche': 'milk',
    'milk': 'leche',
    'queso': 'cheese',
    'cheese': 'queso',
    'mantequilla': 'butter',
    'butter': 'mantequilla',
    'crema': 'cream',
    'cream': 'crema',
    'yogurt': 'yogur',
    'yogur': 'yogurt',
    'helado': 'ice cream mantecado',
    'mantecado': 'ice cream helado',
    'icecream': 'helado mantecado',
    'hielo': 'ice',
    'ice': 'hielo',
    
    // Beverages
    'agua': 'water',
    'water': 'agua',
    'jugo': 'juice',
    'juice': 'jugo',
    'refresco': 'soda soft drink',
    'soda': 'refresco',
    'gaseosa': 'soda refresco',
    'cerveza': 'beer',
    'beer': 'cerveza',
    'vino': 'wine',
    'wine': 'vino',
    'ron': 'rum',
    'rum': 'ron',
    'cafe': 'coffee',
    'coffee': 'cafe',
    'te': 'tea',
    'tea': 'te',
    'chocolate': 'chocolate',
    
    // Grains & Breads
    'pan': 'bread',
    'bread': 'pan',
    'arroz': 'rice',
    'rice': 'arroz',
    'pasta': 'pasta',
    'harina': 'flour',
    'flour': 'harina',
    'cereal': 'cereal',
    'avena': 'oatmeal oats',
    'oatmeal': 'avena',
    'oats': 'avena',
    'galleta': 'cookie cracker',
    'galletas': 'cookies crackers',
    'cookie': 'galleta',
    'cookies': 'galletas',
    'cracker': 'galleta',
    'crackers': 'galletas',
    'bizcocho': 'cake',
    'cake': 'bizcocho pastel',
    'pastel': 'cake pie',
    'pie': 'pastel',
    
    // Condiments & Seasonings
    'azucar': 'sugar',
    'sugar': 'azucar',
    'sal': 'salt',
    'salt': 'sal',
    'aceite': 'oil',
    'oil': 'aceite',
    'vinagre': 'vinegar',
    'vinegar': 'vinagre',
    'mayonesa': 'mayonnaise mayo',
    'mayonnaise': 'mayonesa',
    'mayo': 'mayonesa',
    'mostaza': 'mustard',
    'mustard': 'mostaza',
    'ketchup': 'ketchup catsup',
    'salsa': 'sauce salsa',
    'sauce': 'salsa',
    'pimienta': 'pepper',
    'pepper': 'pimienta',
    'aji': 'pepper hot chile',
    'adobo': 'seasoning adobo',
    'sofrito': 'sofrito',
    
    // Fruits
    'manzana': 'apple',
    'apple': 'manzana',
    'naranja': 'orange',
    'orange': 'naranja china',
    'china': 'orange naranja',
    'platano': 'banana plantain',
    'banana': 'platano guineo',
    'guineo': 'banana',
    'plantain': 'platano',
    'uva': 'grape',
    'grape': 'uva',
    'uvas': 'grapes',
    'grapes': 'uvas',
    'fresa': 'strawberry',
    'strawberry': 'fresa',
    'pera': 'pear',
    'pear': 'pera',
    'melon': 'melon cantaloupe',
    'sandia': 'watermelon',
    'watermelon': 'sandia',
    'piña': 'pineapple',
    'pineapple': 'piña pina',
    'mango': 'mango',
    'limon': 'lemon lime',
    'lemon': 'limon',
    'lime': 'limon',
    'coco': 'coconut',
    'coconut': 'coco',
    'aguacate': 'avocado',
    'avocado': 'aguacate',
    'guayaba': 'guava',
    'guava': 'guayaba',
    'papaya': 'papaya',
    'parcha': 'passion fruit',
    'maracuya': 'passion fruit parcha',
    
    // Vegetables
    'papa': 'potato',
    'papas': 'potatoes',
    'potato': 'papa',
    'potatoes': 'papas',
    'tomate': 'tomato',
    'tomato': 'tomate',
    'cebolla': 'onion',
    'onion': 'cebolla',
    'ajo': 'garlic',
    'garlic': 'ajo',
    'lechuga': 'lettuce',
    'lettuce': 'lechuga',
    'zanahoria': 'carrot',
    'carrot': 'zanahoria',
    'maiz': 'corn',
    'corn': 'maiz',
    'habichuela': 'bean',
    'habichuelas': 'beans',
    'bean': 'habichuela',
    'beans': 'habichuelas frijoles',
    'frijol': 'bean',
    'frijoles': 'beans',
    'guisante': 'pea',
    'pea': 'guisante',
    'peas': 'guisantes',
    'yuca': 'yuca cassava',
    'cassava': 'yuca',
    'batata': 'sweet potato',
    'calabaza': 'pumpkin squash',
    'pumpkin': 'calabaza',
    'squash': 'calabaza',
    'pepino': 'cucumber',
    'cucumber': 'pepino',
    'pimiento': 'pepper bell',
    'bellpepper': 'pimiento',
    'chile': 'pepper hot aji',
    'espinaca': 'spinach',
    'spinach': 'espinaca',
    'brocoli': 'broccoli',
    'broccoli': 'brocoli',
    
    // Snacks & Sweets
    'dulce': 'candy sweet',
    'candy': 'dulce',
    'sweet': 'dulce',
    'caramelo': 'caramel candy',
    'bombon': 'candy chocolate',
    'chicle': 'gum',
    'gum': 'chicle',
    'papitas': 'chips',
    'chips': 'papitas',
    'palomitas': 'popcorn',
    'popcorn': 'palomitas',
    'nuez': 'nut walnut',
    'nut': 'nuez',
    'nuts': 'nueces',
    'nueces': 'nuts',
    'mani': 'peanut',
    'peanut': 'mani',
    'almendra': 'almond',
    'almond': 'almendra',
    
    // Household & Cleaning
    'jabon': 'soap',
    'soap': 'jabon',
    'detergente': 'detergent',
    'detergent': 'detergente',
    'limpiador': 'cleaner',
    'cleaner': 'limpiador',
    'papel': 'paper',
    'paper': 'papel',
    'toalla': 'towel',
    'towel': 'toalla',
    'servilleta': 'napkin',
    'napkin': 'servilleta',
    'bolsa': 'bag',
    'bag': 'bolsa',
    'plato': 'plate dish',
    'plate': 'plato',
    'dish': 'plato',
    'vaso': 'cup glass',
    'cup': 'vaso taza',
    'glass': 'vaso',
    'taza': 'cup mug',
    
    // Cooking & Food Prep
    'congelado': 'frozen',
    'frozen': 'congelado',
    'fresco': 'fresh',
    'fresh': 'fresco',
    'frito': 'fried',
    'fried': 'frito',
    'asado': 'roasted grilled',
    'roasted': 'asado',
    'grilled': 'asado',
    'hervido': 'boiled',
    'boiled': 'hervido',
    'crudo': 'raw',
    'raw': 'crudo',
    'cocido': 'cooked',
    'cooked': 'cocido',
    'entero': 'whole',
    'whole': 'entero',
    'rebanada': 'slice',
    'slice': 'rebanada',
    'picado': 'chopped diced',
    'chopped': 'picado',
    'diced': 'picado',
    'rallado': 'grated shredded',
    'grated': 'rallado',
    'shredded': 'rallado',
    
    // Sizes & Quantities
    'grande': 'large big',
    'large': 'grande',
    'big': 'grande',
    'pequeño': 'small',
    'small': 'pequeño chico',
    'chico': 'small',
    'mediano': 'medium',
    'medium': 'mediano',
    'jumbo': 'jumbo',
    'mini': 'mini small',
    'familiar': 'family size',
    'family': 'familiar',
    
    // Common Product Terms
    'organico': 'organic',
    'organic': 'organico',
    'natural': 'natural',
    'light': 'light ligero',
    'ligero': 'light',
    'bajo': 'low',
    'low': 'bajo',
    'sin': 'without free',
    'without': 'sin',
    'free': 'sin libre',
    'libre': 'free',
    'diet': 'dieta',
    'dieta': 'diet'
  };

  // Normalize search term before sending to backend.
  // The backend's expandSearchTerms() handles all translation expansion server-side —
  // pre-expanding here would join words into a phrase that the backend treats as a
  // single ILIKE pattern, completely bypassing its per-word translation logic.
  const buildApiSearch = (term) => {
    if (!term || !term.trim()) return '';
    return removeAccents(term.trim().toLowerCase());
  };

  // Fire client-side filter (or fallback API call) whenever debounced searchTerm changes.
  // Gated on preloadDone so the initial render waits for the full-catalog fetch and
  // doesn't burn a slow API call we'd immediately overwrite.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Wait for auth to settle before firing a request without a bearer
    if (!BYPASS_AUTH && authLoading) {
      return;
    }

    // Auth done but not authenticated - stop loading, don't fetch
    if (!BYPASS_AUTH && !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Wait until the preload either succeeds (fast path) or fails (API fallback).
    if (!preloadDone) {
      return;
    }

    loadPage(1, buildApiSearch(searchTerm));
  }, [searchTerm, authLoading, isAuthenticated, preloadDone]); // intentionally omits loadPage/buildApiSearch (recreated each render)

  // Fuzzy matching function - calculates similarity between two strings
  // Returns a score from 0 to 1 (1 = exact match, 0 = no similarity)
  const calculateFuzzySimilarity = (input, target) => {
    const s1 = input.toLowerCase();
    const s2 = target.toLowerCase();
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  // Calculate Levenshtein distance between two strings
  const levenshteinDistance = (s1, s2) => {
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  // Get fuzzy matched brands based on input (operates on filteredModalBrands)
  const getFuzzyMatchedBrands = (input) => {
    if (!input.trim()) return filteredModalBrands.filter(b => b !== 'All');
    
    return filteredModalBrands
      .filter(b => b !== 'All')
      .map(brand => ({
        brand,
        score: calculateFuzzySimilarity(input, brand)
      }))
      .filter(item => item.score > 0.4) // Only show matches above 40% similarity
      .sort((a, b) => b.score - a.score)
      .map(item => item.brand);
  };

  // --- FILTERING & SEARCH ---
  // Static list of retailers
  const retailers = [
    'All Retailers',
    'Agranel',
    'Aliadas',
    'Amigo',
    'El Botiquin',
    'Capri',
    'Centro Ahorros',
    'Comercio CC',
    'Coopharma',
    'Costco',
    'CVS',
    'Econo',
    'Famcoop',
    'Farmacias Plaza',
    'Good Neighbor',
    'Fcia Caridad/Pharmamax',
    'Hatillo CC',
    'Mega Fresh',
    'Mi Gente',
    'Mr Special',
    'Napo Velez',
    'Pepe Gangas',
    'Plaza Loiza',
    'Pueblo',
    'Ralph',
    'Sams',
    'Selectos',
    'Supermax',
    'Walgreens',
    'Walmart'
  ];

  // Categories filtered by selected brand
  const categories = useMemo(() => {
    const filteredProducts = filterBrand.length === 0 ? products : products.filter(p => filterBrand.includes(p.brand));
    return ['All', ...new Set(filteredProducts.map(p => p.category).filter(Boolean))].sort();
  }, [products, filterBrand]);

  // Brands filtered by selected category
  const brands = useMemo(() => {
    const filteredProducts = filterCategory.length === 0 ? products : products.filter(p => filterCategory.includes(p.category));
    return ['All', ...new Set(filteredProducts.map(p => p.brand).filter(Boolean))].sort();
  }, [products, filterCategory]);

  // Sizes filtered by selected brand and category
  const sizes = useMemo(() => {
    let filteredProducts = products;
    if (filterBrand.length > 0) filteredProducts = filteredProducts.filter(p => filterBrand.includes(p.brand));
    if (filterCategory.length > 0) filteredProducts = filteredProducts.filter(p => filterCategory.includes(p.category));
    const sizeStrings = filteredProducts.map(p => [p.form, p.size, p.uom].filter(Boolean).join(' ')).filter(Boolean);
    return ['All', ...new Set(sizeStrings)].sort();
  }, [products, filterBrand, filterCategory]);

  // Temp categories for modal (filtered by temp brand selection)
  const tempCategories = useMemo(() => {
    const filtered = tempFilterBrand.length === 0 ? products : products.filter(p => tempFilterBrand.includes(p.brand));
    return [...new Set(filtered.map(p => p.category).filter(Boolean))].sort();
  }, [products, tempFilterBrand]);

  // Temp sizes for modal (filtered by temp brand and category selection)
  const tempSizes = useMemo(() => {
    let filtered = products;
    if (tempFilterBrand.length > 0) filtered = filtered.filter(p => tempFilterBrand.includes(p.brand));
    if (tempFilterCategory.length > 0) filtered = filtered.filter(p => tempFilterCategory.includes(p.category));
    const sizeStrings = filtered.map(p => [p.form, p.size, p.uom].filter(Boolean).join(' ')).filter(Boolean);
    return [...new Set(sizeStrings)].sort();
  }, [products, tempFilterBrand, tempFilterCategory]);

  // Calculate result count for temp filters in modal
  // Filter modal options based on searchInput (cascading search)
  // First filter products by search, then extract available options from results
  const searchFilteredProducts = useMemo(() => {
    if (!searchInput.trim()) return products;
    
    return products.filter(product => {
      const searchLower = searchInput.toLowerCase();
      const upc = (product.upc || '').toLowerCase();
      const brand = (product.brand || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      const category = (product.category || '').toLowerCase();
      
      return upc.includes(searchLower) || 
             brand.includes(searchLower) || 
             description.includes(searchLower) || 
             category.includes(searchLower);
    });
  }, [products, searchInput]);

  // Step 1: Filter by selected brand (cascades from search results)
  const step1FilteredProducts = useMemo(() => {
    if (tempFilterBrand.length === 0) return searchFilteredProducts;
    return searchFilteredProducts.filter(p => tempFilterBrand.includes(p.brand));
  }, [searchFilteredProducts, tempFilterBrand]);

  // Step 2: Filter by selected category (cascades from step 1)
  const step2FilteredProducts = useMemo(() => {
    if (tempFilterCategory.length === 0) return step1FilteredProducts;
    return step1FilteredProducts.filter(p => tempFilterCategory.includes(p.category));
  }, [step1FilteredProducts, tempFilterCategory]);

  const filteredModalBrands = useMemo(() => {
    const availableBrands = ['All', ...new Set(searchFilteredProducts.map(p => p.brand).filter(Boolean))].sort();
    return availableBrands;
  }, [searchFilteredProducts]);

  const filteredModalCategories = useMemo(() => {
    const availableCategories = [...new Set(step1FilteredProducts.map(p => p.category).filter(Boolean))].sort();
    return availableCategories;
  }, [step1FilteredProducts]);

  const filteredModalSizes = useMemo(() => {
    const availableSizes = [...new Set(step2FilteredProducts
      .map(p => [p.form, p.size, p.uom].filter(Boolean).join(' '))
      .filter(Boolean)
    )].sort();
    return availableSizes;
  }, [step2FilteredProducts]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesCategory = filterCategory.length === 0 || filterCategory.includes(product.category);
      const matchesBrand = filterBrand.length === 0 || filterBrand.includes(product.brand);
      const productSize = [product.form, product.size, product.uom].filter(Boolean).join(' ');
      const matchesSize = filterSize.length === 0 || filterSize.includes(productSize);
      const matchesUpc = filterByUpc.length === 0 || filterByUpc.includes(product.upc);

      return matchesCategory && matchesBrand && matchesSize && matchesUpc;
    });
  }, [products, filterCategory, filterBrand, filterSize, filterByUpc]);

  // Apply sorting to filtered products
  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) {
      return [...filteredProducts].sort((a, b) => {
        // When rows carry a relevance _rank (from a search query), honor it first.
        // Tiebreak by id DESC, matching the server's ORDER BY _rank, id DESC.
        if (a._rank !== undefined && b._rank !== undefined && a._rank !== b._rank) {
          return a._rank - b._rank;
        }
        const aId = Number(a.id);
        const bId = Number(b.id);

        if (Number.isFinite(aId) && Number.isFinite(bId)) {
          return bId - aId;
        }

        return String(b.id || '').localeCompare(String(a.id || ''), undefined, { numeric: true });
      });
    }

    const sorted = [...filteredProducts].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      // Handle numeric sorting for Clients field
      if (sortConfig.key === 'Clients') {
        const aNum = parseInt(aValue) || 0;
        const bNum = parseInt(bValue) || 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String sorting
      const comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredProducts, sortConfig]);

  // Pagination
  // totalPages derived from API total count; currentPage is set by loadPage
  const totalPages = Math.ceil((catalogStats.total || 0) / PAGE_SIZE);
  const paginatedProducts = sortedProducts; // already a single page from API

  // Reset sorting when search term changes
  useEffect(() => {
    setSortConfig({ key: null, direction: 'asc' });
    setDisabledProducts(new Set()); // Re-enable all products on new search
  }, [searchTerm]);

  // Keyboard shortcuts for filter modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape key closes modal and resets search state
      if (e.key === 'Escape' && showFilterModal) {
        setShowFilterModal(false);
        setBrandSearchInput('');
        setSelectedBrandIndex(-1);
        setShowBrandDropdown(false);
      }
      // Ctrl/Cmd + K opens modal and focuses brand filter
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowFilterModal(true);
        setTimeout(() => document.getElementById('modal-brand-input')?.focus(), 100);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFilterModal]);

  // Sync temp filters when modal opens
  useEffect(() => {
    if (showFilterModal) {
      setTempFilterBrand(filterBrand);
      setTempFilterCategory(filterCategory);
      setTempFilterSize(filterSize);
      
      // Determine correct modal step based on current filters
      if (filterSize.length > 0) setModalStep(3);
      else if (filterCategory.length > 0) setModalStep(2);
      else if (filterBrand.length > 0) setModalStep(2);
      else setModalStep(1);
      
      // Focus brand input after modal renders (only works if brands >10 and searchable input shown)
      setTimeout(() => document.getElementById('modal-brand-input')?.focus(), 100);
    }
  }, [showFilterModal, filterBrand, filterCategory, filterSize]);

  // --- SORTING AND RESIZING FUNCTIONS ---
  const handleSort = (columnKey) => {
    setSortConfig(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: Math.max(50, newWidth) // Minimum width of 50px
    }));
  };

  // --- ACTIONS ---
  const copyUPCToClipboard = async (upc, productId) => {
    try {
      // Copy just the UPC - web forms can't auto-distribute multi-value clipboard data
      // Pricing values are displayed in the card above for manual reference
      const copyData = upc;
      
      // Try modern Clipboard API first (requires HTTPS)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(copyData);
      } else {
        // Fallback for non-secure contexts (HTTP) using legacy execCommand
        const textArea = document.createElement('textarea');
        textArea.value = copyData;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setClipboardToast(upc);
      setTimeout(() => setClipboardToast(null), 2000);
      
      // Add product to disabled set
      setDisabledProducts(prev => new Set([...prev, upc]));
    } catch (error) {
      console.error('Failed to copy UPC:', error);
    }
  };

  const openCommentModal = (product) => {
    setCommentError(null);
    setIsSavingComment(false);
    setCommentModalProduct(product);
    setProductComment(product.comment || '');
  };

  const saveComment = async () => {
    if (!commentModalProduct || isSavingComment) return;

    setIsSavingComment(true);
    setCommentError(null);

    const { error } = await insertIssue({
      source: 'pm_comment',
      upc: commentModalProduct.upc,
      description: commentModalProduct.description ?? '',
      comment: productComment,
      user_id: user?.sub ?? null,
      user_email: user?.email ?? null,
    });

    if (error) {
      setCommentError('Could not save the comment ticket. Please try again.');
      setIsSavingComment(false);
      return;
    }

    setProducts(prev => 
      prev.map(p => 
        p._originalIndex === commentModalProduct._originalIndex 
          ? { ...p, comment: productComment }
          : p
      )
    );
    setCommentModalProduct(null);
    setProductComment('');
    setCommentError(null);
    setIsSavingComment(false);
  };

  const openNewProductForm = () => {
    const prefilled = {
      spotNumberLink: '',
      suggestedDescription: searchInput || '',
      referenceLink: '',
      varietyType: ''
    };
    setNewProductError(null);
    setSpotLinkError(null);
    setIsSavingNewProduct(false);
    setNewProduct(prefilled);
    setShowNewProductForm(true);
  };

  const handleNewProductChange = (field, value) => {
    setNewProduct(prev => ({ ...prev, [field]: value }));
    // Typing in the field is the user acting on the complaint — drop it and
    // re-check on blur, so the message doesn't nag mid-edit.
    if (field === 'spotNumberLink') setSpotLinkError(null);
  };

  const handleSpotLinkBlur = () => {
    const { error } = validateSpotLink(newProduct.spotNumberLink);
    setSpotLinkError(error);
  };

  const saveNewProduct = async () => {
    if (isSavingNewProduct) return;

    // Validation
    if (!newProduct.varietyType) {
      setNewProductError('Variety Type is required. Please select an option.');
      return;
    }

    // Spot Link stays optional, but anything typed in it has to be a real spot
    // link — a malformed one is worse than a blank, since New Products Requests
    // renders it as a dead link and can't look the spot up.
    const spotLink = validateSpotLink(newProduct.spotNumberLink);
    if (!spotLink.valid) {
      setSpotLinkError(spotLink.error);
      return;
    }
    setSpotLinkError(null);

    setIsSavingNewProduct(true);
    setNewProductError(null);

    const issuePayload = {
      source: 'pm_new_product',
      // Normalized, so every stored link is the canonical form regardless of
      // which variant was pasted.
      spot_number_link: spotLink.normalized,
      suggested_description: newProduct.suggestedDescription ?? '',
      reference_link: newProduct.referenceLink ?? '',
      level: newProduct.varietyType, // Store variety type in level column
      user_id: user?.sub ?? null,
      user_email: user?.email ?? null,
    };

    const { error } = await insertIssue(issuePayload);

    if (error) {
      setNewProductError('Could not create product request. Please try again.');
      setIsSavingNewProduct(false);
      return;
    }

    const productToAdd = {
      ...newProduct,
      _originalIndex: newProducts.length
    };
    setNewProducts(prev => [...prev, productToAdd]); // Store in separate state

    setIsSavingNewProduct(false);
    setShowNewProductForm(false);
    setNewProduct({});
  };

  const clearFilters = async () => {
    setSearchInput('');
    setSearchTerm('');
    setFilterCategory([]);
    setFilterBrand([]);
    setFilterSize([]);
    setFilterByUpc([]);
    setBrandSearchInput('');
    setShowBrandDropdown(false);
    setTempFilterBrand([]);
    setTempFilterCategory([]);
    setTempFilterSize([]);
    setModalStep(1);
    setSortConfig({ key: null, direction: 'asc' });
    setDisabledProducts(new Set()); // Re-enable all products
    setIsUpcSearchMode(false);
    await loadPage(1, ''); // Reload normal pagination
  };

  const applyExtractedUpcsAndCloseModal = async () => {
    if (extractedSpotUpcs.length > 0) {
      // Keep filterByUpc for client-side exact matching and purple badge display
      setFilterByUpc(extractedSpotUpcs);
      
      // Enable UPC search mode to hide blue search badge
      setIsUpcSearchMode(true);
      
      // Clear blue filters when UPC filter is applied
      setFilterBrand([]);
      setFilterCategory([]);
      setFilterSize([]);
      setSearchInput('');
      setSearchTerm('');
      
      // Search for UPCs server-side with parallel requests
      await searchProductsByUpcs(extractedSpotUpcs);
    }
    setShowSpotClipboardModal(false);
    setSpotClipboardText('');
  };

  // Direct spot search via API (supports single or multiple comma-separated IDs)
  const handleDirectSpotSearch = async (spotInput) => {
    // Parse comma-separated spot IDs
    const spotIds = spotInput
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(id => !isNaN(id) && id > 0);

    if (spotIds.length === 0) {
      alert('Please enter valid spot ID(s)');
      return;
    }

    setIsLoading(true);
    setLoadingStatus(`Fetching products for ${spotIds.length} spot(s)...`);
    setApiError(false);

    try {
      const result = await fetchSpotProducts(spotIds);

      if (!result || result.spots.length === 0) {
        setLoadingStatus(`No products found for spot(s) ${spotIds.join(', ')}`);
        setIsLoading(false);
        setTimeout(() => setIsLoading(false), 2000);
        return;
      }

      // Extract all UPCs from all spots
      const upcs = result.spots
        .flatMap(spot => spot.products || [])
        .map(p => p.upc)
        .filter(Boolean);

      const uniqueUpcs = [...new Set(upcs)];
      setFilterByUpc(uniqueUpcs);
      setIsUpcSearchMode(true);

      // Clear other filters
      setFilterBrand([]);
      setFilterCategory([]);
      setFilterSize([]);
      setSearchInput('');
      setSearchTerm('');

      // Search for full product details
      await searchProductsByUpcs(uniqueUpcs);

      setSpotSearchInput('');
    } catch (error) {
      console.error('Spot search failed:', error);
      setApiError(true);
      setLoadingStatus('Failed to fetch spot products');
      setTimeout(() => setIsLoading(false), 2000);
    } finally {
      if (!apiError) {
        setIsLoading(false);
      }
    }
  };

  const handleAutoPasteSpot = async () => {
    try {
      let clipboardText = '';
      
      // Try to read from clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        clipboardText = await navigator.clipboard.readText();
      } else {
        // Fallback: prompt user to paste
        const userInput = prompt('Please paste the clipboard content:');
        if (!userInput) return;
        clipboardText = userInput;
      }
      
      // Parse UPCs from clipboard text
      const marker = 'Product(s) in this spot:';
      const markerIndex = clipboardText.indexOf(marker);
      
      if (markerIndex === -1) {
        alert('No "Product(s) in this spot:" marker found in clipboard.');
        return;
      }
      
      const sectionText = clipboardText.slice(markerIndex + marker.length);
      const regex = /upc:\s*([^\]]+)\]/gi;
      const upcs = [];
      let match;
      
      while ((match = regex.exec(sectionText)) !== null) {
        const cleanedUpc = (match[1] || '').trim();
        if (cleanedUpc) upcs.push(cleanedUpc);
      }
      
      if (upcs.length === 0) {
        alert('No UPCs found in the clipboard content.');
        return;
      }
      
      // Apply filters with unique UPCs
      const uniqueUpcs = [...new Set(upcs)];
      
      // Keep filterByUpc for client-side exact matching and purple badge display
      setFilterByUpc(uniqueUpcs);
      
      // Enable UPC search mode to hide blue search badge
      setIsUpcSearchMode(true);
      
      // Clear blue filters when UPC filter is applied
      setFilterBrand([]);
      setFilterCategory([]);
      setFilterSize([]);
      setSearchInput('');
      setSearchTerm('');
      
      // Search for UPCs server-side with parallel requests
      await searchProductsByUpcs(uniqueUpcs);
      
      // Close modal and clear
      setShowSpotClipboardModal(false);
      setSpotClipboardText('');
      setSpotSearchInput('');
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      alert('Failed to read clipboard. Please try again.');
    }
  };

  const applyModalFiltersAndClose = async (sizeSelection = tempFilterSize) => {
    // Update filter state
    setFilterBrand(tempFilterBrand);
    setFilterCategory(tempFilterCategory);
    setFilterSize(sizeSelection);
    setTempFilterSize(sizeSelection);
    setBrandSearchInput('');
    setShowBrandDropdown(false);
    setShowFilterModal(false);
    setSelectedBrandIndex(-1);

    // Fetch filtered results from API (server-side filtering)
    const filters = {
      brand: tempFilterBrand.length === 1 ? tempFilterBrand[0] : null,
      category: tempFilterCategory.length === 1 ? tempFilterCategory[0] : null,
      size: sizeSelection.length === 1 ? sizeSelection[0] : null,
    };
    await loadPage(1, searchTerm, filters);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };

  const exportToCSV = () => {
    let csvContent = '';
    
    // Table 1: New Products
    if (newProducts.length > 0) {
      csvContent += '=== NEW PRODUCTS ===\n';
      const newProductHeaders = Object.keys(newProducts[0]).filter(key => key !== '_originalIndex');
      csvContent += newProductHeaders.join(',') + '\n';
      
      newProducts.forEach(product => {
        const row = newProductHeaders.map(header => {
          const value = String(product[header] || '');
          return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',');
        csvContent += row + '\n';
      });
      csvContent += '\n\n';
    }
    
    // Table 2: Products with Comments
    const productsWithComments = products.filter(p => p.comment && p.comment.trim() !== '');
    if (productsWithComments.length > 0) {
      csvContent += '=== PRODUCTS WITH COMMENTS ===\n';
      
      // Get all headers and insert Comment after UPC
      const allHeaders = Object.keys(productsWithComments[0]).filter(key => key !== '_originalIndex');
      const upcIndex = allHeaders.indexOf('upc');
      const headersWithComment = [...allHeaders];
      
      // Insert Comment column after UPC if UPC exists
      if (upcIndex !== -1 && !allHeaders.includes('Comment')) {
        headersWithComment.splice(upcIndex + 1, 0, 'Comment');
      } else if (!allHeaders.includes('Comment')) {
        headersWithComment.push('Comment');
      }
      
      csvContent += headersWithComment.join(',') + '\n';
      
      productsWithComments.forEach(product => {
        const row = headersWithComment.map(header => {
          let value = '';
          if (header === 'Comment') {
            value = String(product.comment || '');
          } else {
            value = String(product[header] || '');
          }
          return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',');
        csvContent += row + '\n';
      });
    }
    
    // If no data to export, show message
    if (newProducts.length === 0 && productsWithComments.length === 0) {
      alert('No new products or comments to export!');
      return;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `ShopWise_Export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-neutral-50 font-sans">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-neutral-800 flex items-center gap-2">
                  <Package className="text-primary-600" size={28} />
                  ShopWise Product Manager
                </h1>
                <p className="text-neutral-500 text-sm">Search and edit your product catalog</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={openNewProductForm}
                className="flex items-center gap-2 bg-success-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-success-700 transition-colors shadow-sm"
              >
                <Plus size={18} /> Create New Product
              </button>
            </div>
          </div>

          {/* Error Display */}
          {loadError && (
            <div className="mt-4 p-4 bg-error-50 border border-error-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-error-600 flex-shrink-0" size={20} />
              <div>
                <p className="text-error-800 font-medium">Upload Error</p>
                <p className="text-error-700 text-sm">{loadError}</p>
              </div>
              <button 
                onClick={() => setLoadError(null)}
                className="ml-auto text-error-600 hover:text-error-800"
              >
                <X size={20} />
              </button>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex flex-col gap-4">
            {/* Top Bar - Retailer + Toggle + Spot Search */}
            <div className="flex gap-3 items-center flex-nowrap">
              {/* Retailer Dropdown - Always Visible */}
              <div className="relative min-w-0 flex-shrink-0">
                <select 
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-success-500 bg-white min-w-[160px] ${
                    filterRetailer !== 'All Retailers' ? 'border-success-500 bg-success-50 font-semibold' : 'border-neutral-200'
                  }`}
                  value={filterRetailer}
                  onChange={(e) => setFilterRetailer(e.target.value)}
                >
                  {retailers.map(retailer => (
                    <option key={retailer} value={retailer}>{retailer}</option>
                  ))}
                </select>
              </div>

              {/* Search Mode Toggle */}
              <div className="flex items-center gap-3 px-4 py-2.5 border border-neutral-200 rounded-lg bg-white flex-shrink-0">
                <span className={`text-xs font-medium whitespace-nowrap ${searchMode === 'simple' ? 'text-neutral-600' : 'text-neutral-400'}`}>
                  Simple
                </span>
                <button
                  onClick={() => setSearchMode(searchMode === 'simple' ? 'advanced' : 'simple')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    searchMode === 'advanced' ? 'bg-primary-600' : 'bg-neutral-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      searchMode === 'advanced' ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className={`text-xs font-medium whitespace-nowrap ${searchMode === 'advanced' ? 'text-neutral-600' : 'text-neutral-400'}`}>
                  Advanced
                </span>
              </div>

              {/* Spot Search */}
              <div className="relative flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Spot Search"
                    className="w-auto min-w-[140px] px-4 pr-9 py-2.5 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                    value={spotSearchInput}
                    onChange={(e) => {
                      // Allow digits and commas for multiple spot IDs
                      const allowedChars = e.target.value.replace(/[^0-9,\s]/g, '');
                      setSpotSearchInput(allowedChars);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      if (!spotSearchInput.trim()) return;

                      // Call direct API search
                      handleDirectSpotSearch(spotSearchInput);
                    }}
                  />
                  {spotSearchInput && (
                    <button
                      onClick={() => setSpotSearchInput('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                {/* Fallback: External link to manual clipboard workflow */}
                {spotSearchInput && (
                  <button
                    onClick={() => {
                      const firstSpotId = spotSearchInput.split(',')[0].trim();
                      const value = parseInt(firstSpotId, 10);
                      if (Number.isInteger(value) && value > 0 && value <= 3000000) {
                        setShowSpotClipboardModal(true);
                        window.open(buildSpotUrl(value), '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className="p-2.5 border border-neutral-200 rounded-lg text-neutral-400 hover:text-primary-500 hover:border-primary-500 transition-colors bg-white"
                    title="Open external spot viewer (fallback)"
                  >
                    <ExternalLink size={16} />
                  </button>
                )}
              </div>

            </div>

            {/* Advanced Search Box (opens modal) */}
            {searchMode === 'advanced' && (
              <button 
                onClick={() => {
                  setShowFilterModal(true);
                  setTimeout(() => document.getElementById('modal-brand-input')?.focus(), 100);
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 border border-neutral-200 rounded-lg text-sm text-neutral-400 hover:border-primary-500 hover:text-neutral-600 transition-colors bg-white"
              >
                <Search size={20} />
                <span>Search products...</span>
              </button>
            )}

            {/* Simple Search Input */}
            {searchMode === 'simple' && (
              <div className="flex gap-4 items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search by UPC, brand, description, category..."
                    className="w-full pl-10 pr-10 py-2.5 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      setIsUpcSearchMode(false);
                    }}
                  />
                  {searchInput && (
                    <button
                      onClick={() => {
                        setSearchInput('');
                        setSearchTerm('');
                        setIsUpcSearchMode(false);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                {/* Search Help Tooltip */}
                <div className="relative group">
                  <button
                    type="button"
                    className="p-2 text-neutral-400 hover:text-primary-600 transition-colors"
                    aria-label="Search tips"
                  >
                    <HelpCircle size={20} />
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-80 p-4 bg-white border border-neutral-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <h4 className="font-semibold text-neutral-900 mb-2">Search Tips</h4>
                    <ul className="space-y-1.5 text-sm text-neutral-600">
                      <li><span className="font-medium">Type any words</span> — finds products with ALL words</li>
                      <li><span className="font-medium">Order doesn't matter</span> — "beef jerky" = "jerky beef"</li>
                      <li><span className="font-medium">Case doesn't matter</span> — "Beef" = "BEEF" = "beef"</li>
                      <li><span className="font-medium">Accents don't matter</span> — "café" finds "cafe"</li>
                      <li><span className="font-medium">Spanish & English</span> — "chicken" also finds "pollo"</li>
                    </ul>
                    <div className="mt-3 pt-3 border-t border-neutral-100">
                      <p className="text-xs text-neutral-500 font-medium mb-1">Examples:</p>
                      <div className="text-xs text-neutral-600 space-y-0.5">
                        <p><code className="bg-neutral-100 px-1 rounded">colgate</code> → "colgate" anywhere</p>
                        <p><code className="bg-neutral-100 px-1 rounded">colgate fresh</code> → both words present</p>
                        <p><code className="bg-neutral-100 px-1 rounded">leche</code> → "leche" or "milk"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Active Search Badge (Simple Mode) */}
            {searchMode === 'simple' && searchTerm && !isUpcSearchMode && (
              <div className="flex gap-2 flex-wrap items-center">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => {
                      setSearchInput('');
                      setSearchTerm('');
                      setIsUpcSearchMode(false);
                    }}
                    className="hover:text-primary-900"
                  >
                    <X size={14} />
                  </button>
                </span>
                <button
                  onClick={() => {
                    setSearchInput('');
                    setSearchTerm('');
                    setIsUpcSearchMode(false);
                  }}
                  className="px-3 py-1 text-sm font-semibold text-error-600 hover:text-error-700 hover:bg-error-50 rounded-lg transition-colors"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Active Spot/UPC Badge (Simple Mode) */}
            {searchMode === 'simple' && isUpcSearchMode && filterByUpc.length > 0 && (
              <div className="flex gap-2 flex-wrap items-center">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  Spot products: {filterByUpc.length}
                  <button
                    onClick={async () => {
                      setFilterByUpc([]);
                      setSearchInput('');
                      setSearchTerm('');
                      setIsUpcSearchMode(false);
                      setDisabledProducts(new Set());
                      await loadPage(1, '');
                    }}
                    className="hover:text-purple-900"
                  >
                    <X size={14} />
                  </button>
                </span>
                <button
                  onClick={async () => {
                    setFilterByUpc([]);
                    setSearchInput('');
                    setSearchTerm('');
                    setIsUpcSearchMode(false);
                    setDisabledProducts(new Set());
                    await loadPage(1, '');
                  }}
                  className="px-3 py-1 text-sm font-semibold text-error-600 hover:text-error-700 hover:bg-error-50 rounded-lg transition-colors"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Active Filter Badges (Advanced Mode) */}
            {searchMode === 'advanced' && (filterBrand.length > 0 || filterCategory.length > 0 || filterSize.length > 0 || searchTerm || filterByUpc.length > 0) && (
              <div className="flex gap-2 flex-wrap items-center">
                {filterBrand.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                    {filterBrand.length === 1 ? 'Brand' : 'Brands'}: {filterBrand.join(', ')}
                    <button 
                      onClick={() => { 
                        setFilterBrand([]); 
                      }} 
                      className="hover:text-primary-900"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filterCategory.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                    {filterCategory.length === 1 ? 'Category' : 'Categories'}: {filterCategory.join(', ')}
                    <button 
                      onClick={() => { 
                        setFilterCategory([]);
                      }} 
                      className="hover:text-primary-900"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filterSize.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                    {filterSize.length === 1 ? 'Size' : 'Sizes'}: {filterSize.join(', ')}
                    <button 
                      onClick={() => setFilterSize([])} 
                      className="hover:text-primary-900"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {searchTerm && !isUpcSearchMode && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                    Search: "{searchTerm}"
                    <button 
                      onClick={() => {
                        setSearchInput('');
                        setSearchTerm('');
                      }} 
                      className="hover:text-primary-900"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filterByUpc.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {filterByUpc.length === 1 ? 'UPC' : 'UPCs'}: {filterByUpc.length} selected
                    <button 
                      onClick={async () => {
                        setFilterByUpc([]);
                        setSearchInput('');
                        setSearchTerm('');
                        setIsUpcSearchMode(false);
                        setDisabledProducts(new Set()); // Clear disabled products
                        await loadPage(1, ''); // Reload normal pagination
                      }} 
                      className="hover:text-purple-900"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                {sortConfig.key && (
                  <button
                    onClick={() => setSortConfig({ key: null, direction: 'asc' })}
                    className="px-3 py-1 text-sm font-semibold text-warning-600 hover:text-warning-700 hover:bg-warning-50 rounded-lg transition-colors"
                  >
                    Clear Sort
                  </button>
                )}
                <button
                  onClick={clearFilters}
                  className="px-3 py-1 text-sm font-semibold text-error-600 hover:text-error-700 hover:bg-error-50 rounded-lg transition-colors"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
          
          {/* Results count */}
          <div className="mt-3 text-sm text-neutral-600">
            Showing {paginatedProducts.length.toLocaleString()} items on page {currentPage} of {totalPages || 1}
            {catalogStats.total > 0 && ` — ${catalogStats.total.toLocaleString()} total in catalog`}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-6 py-6">
        {(!BYPASS_AUTH && authLoading) || isLoading ? (
          <ProductsManagerLoadingState
            loadingStatus={authLoading ? 'Authenticating...' : loadingStatus}
            loadedCount={catalogStats.loaded}
            totalCount={catalogStats.total}
          />
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-neutral-200 rounded-2xl bg-white">
            <Package size={64} className="text-neutral-200 mb-4" />
            {apiError ? (
              <>
                <p className="text-neutral-600 font-semibold text-lg mb-2">API Unavailable</p>
                <p className="text-neutral-400 font-medium mb-6">Could not connect to the product catalog. Use CSV import as fallback.</p>
              </>
            ) : (
              <>
                <p className="text-neutral-600 font-semibold text-lg mb-2">Welcome to Products Manager</p>
                <p className="text-neutral-400 font-medium mb-6">The product catalog is empty. CSV import is currently disabled.</p>
              </>
            )}
            {apiError ? (
              <label className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 cursor-pointer transition-colors shadow-sm">
                <Upload size={20} /> {isUploading ? 'Uploading...' : 'Import CSV File'}
                <input type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} />
              </label>
            ) : (
              <button
                type="button"
                disabled
                className="flex items-center gap-2 bg-neutral-200 text-neutral-500 px-6 py-3 rounded-lg font-semibold cursor-not-allowed shadow-sm"
                title="CSV import is only enabled when API is unavailable"
              >
                <Upload size={20} /> Import CSV Disabled
              </button>
            )}
            <p className="text-neutral-400 text-xs mt-4 max-w-md text-center">
              Product creation and comments are sent directly to the database API.
            </p>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Product List */}
            <div className="flex-1">
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed" aria-busy={isLoading}>
                    <thead className="bg-neutral-50 border-b border-neutral-200">
                      <tr>
                        <ResizableSortableTableHeader
                          columnKey="actions"
                          label="Actions"
                          width={columnWidths.actions}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          onResize={handleColumnResize}
                          sortable={false}
                        />
                        <ResizableSortableTableHeader
                          columnKey="description"
                          label="Description"
                          width={columnWidths.description}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          onResize={handleColumnResize}
                        />
                        <ResizableSortableTableHeader
                          columnKey="category"
                          label="Category"
                          width={columnWidths.category}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          onResize={handleColumnResize}
                        />
                        <ResizableSortableTableHeader
                          columnKey="size"
                          label="Size"
                          width={columnWidths.size}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          onResize={handleColumnResize}
                        />
                        <ResizableSortableTableHeader
                          columnKey="form"
                          label="Form"
                          width={columnWidths.form}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          onResize={handleColumnResize}
                        />
                        <ResizableSortableTableHeader
                          columnKey="uom"
                          label="UOM"
                          width={columnWidths.uom}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          onResize={handleColumnResize}
                        />
                        {/* Relevance / Status columns disabled: not available in RDS catalog table */}
                        {SHOW_CATALOG_EXTRAS && (
                          <ResizableSortableTableHeader
                            columnKey="Clients"
                            label="Relevance"
                            width={columnWidths.relevance}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onResize={handleColumnResize}
                          />
                        )}
                        {SHOW_CATALOG_EXTRAS && (
                          <ResizableSortableTableHeader
                            columnKey="status"
                            label="Status"
                            width={columnWidths.status}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onResize={handleColumnResize}
                            className="text-center"
                            sortable={false}
                          />
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {paginatedProducts.map((product, index) => {
                        return (
                        <tr 
                          key={product._originalIndex || index}
                          className={`transition-colors ${
                            disabledProducts.has(product.upc)
                              ? 'bg-neutral-100 opacity-60 cursor-not-allowed'
                              : 'hover:bg-primary-50 cursor-pointer'
                          } ${
                            selectedProduct?._originalIndex === product._originalIndex ? 'bg-primary-50' : ''
                          }`}
                          onClick={() => {
                            if (!disabledProducts.has(product.upc)) {
                              copyUPCToClipboard(product.upc, product._originalIndex);
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            if (disabledProducts.has(product.upc)) {
                              setConfirmReEnableProduct(product);
                            }
                          }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectProduct(product);
                                }}
                                className="text-primary-600 hover:text-primary-800 p-1"
                                title="View Details"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setHistoryUsedUpcs(new Set());
                                  setHistoryProduct(product);
                                }}
                                className="text-success-600 hover:text-success-800 p-1"
                                title="View Product History"
                              >
                                <History size={16} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCommentModal(product);
                                }}
                                className={`p-1 ${
                                  product.comment 
                                    ? 'text-warning-600 hover:text-warning-800' 
                                    : 'text-neutral-400 hover:text-neutral-600'
                                }`}
                                title={product.comment ? 'Edit Comment' : 'Add Comment'}
                              >
                                <MessageSquare size={16} />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-600 max-w-md">
                            <div className="line-clamp-3 whitespace-normal leading-relaxed">
                              {product.description}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-600">{product.category}</td>
                          <td className="px-4 py-3 text-sm text-neutral-600">{product.size || '-'}</td>
                          <td className="px-4 py-3 text-sm text-neutral-600">{product.form || '-'}</td>
                          <td className="px-4 py-3 text-sm text-neutral-600">{product.uom || '-'}</td>
                          {/* Relevance / Status cells disabled: not available in RDS catalog table */}
                          {SHOW_CATALOG_EXTRAS && (
                            <td className="px-4 py-3">
                              {product.Clients ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-20 bg-neutral-200 rounded-full h-2 overflow-hidden">
                                    <div 
                                      className={`h-full transition-all ${
                                        parseInt(product.Clients) >= 15 ? 'bg-success-500' :
                                        parseInt(product.Clients) >= 8 ? 'bg-yellow-500' :
                                        'bg-error-500'
                                      }`}
                                      style={{ width: `${Math.min(100, (parseInt(product.Clients) / 20) * 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-semibold text-neutral-600 w-6">{product.Clients}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-neutral-400">-</span>
                              )}
                            </td>
                          )}
                          {SHOW_CATALOG_EXTRAS && (
                            <td className="px-4 py-3 text-center">
                              {(product['REESA Product?'] === 'TRUE' || product['REESA Product?'] === 'Yes' || product['REESA Product?'] === '1' || product['REESA Product?'] === true) ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold bg-primary-100 text-primary-700">
                                  REESA
                                </span>
                              ) : product['REESA Product?'] ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold bg-neutral-100 text-neutral-500">
                                  -
                                </span>
                              ) : null}
                            </td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between bg-neutral-50">
                    <div className="text-sm text-neutral-600">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadPage(currentPage - 1, currentApiSearch)}
                        disabled={currentPage === 1 || isLoading}
                        className="p-2 rounded hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        onClick={() => loadPage(currentPage + 1, currentApiSearch)}
                        disabled={currentPage === totalPages || isLoading}
                        className="p-2 rounded hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Details Viewer */}
            {selectedProduct && (
              <div className="w-[500px] flex-shrink-0">
                <div className="bg-white rounded-lg shadow-sm border border-neutral-200 sticky top-24">
                  <div className="p-4 border-b border-neutral-200 flex items-center justify-between bg-neutral-50">
                    <h3 className="font-bold text-neutral-800 flex items-center gap-2">
                      <Eye size={18} className="text-primary-600" />
                      Product Details
                    </h3>
                    <button 
                      onClick={() => setSelectedProduct(null)}
                      className="text-neutral-400 hover:text-neutral-600"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto space-y-3">
                    {Object.entries(selectedProduct)
                      .filter(([key]) => key !== '_originalIndex' && key !== '_searchScore')
                      .map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                            {key.replace(/_/g, ' ')}
                          </label>
                          <div className="px-3 py-2 bg-neutral-50 rounded text-sm text-neutral-700 border border-neutral-200 min-h-[38px] break-words select-text cursor-text">
                            {value || <span className="text-neutral-400">N/A</span>}
                          </div>
                        </div>
                      ))}
                  </div>

                  <div className="p-4 border-t border-neutral-200 bg-neutral-50">
                    <div className="text-xs text-neutral-500 text-center">
                      Click any field value to select and copy
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clipboard Toast */}
      {clipboardToast && (
        <div className="fixed bottom-6 right-6 bg-neutral-900 text-white px-4 py-3 rounded-lg shadow-2xl flex items-center gap-3 animate-fade-in z-[60]">
          <Package size={18} className="text-primary-400" />
          <div>
            <div className="text-sm font-semibold">UPC Copied!</div>
            <div className="text-xs text-neutral-300 font-mono">{clipboardToast}</div>
          </div>
        </div>
      )}

      {/* Advanced Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <h2 className="text-xl font-bold text-neutral-800">Filter Products</h2>
              <button
                onClick={() => {
                  setShowFilterModal(false);
                  setSelectedBrandIndex(-1);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-6">
              {/* Breadcrumbs */}
              <div className="mb-6 flex items-center gap-2 text-sm">
                <button 
                  onClick={() => setModalStep(1)}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    modalStep === 1 
                      ? 'bg-primary-500 text-white font-medium' 
                      : tempFilterBrand.length > 0
                        ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        : 'bg-neutral-100 text-neutral-400'
                  }`}
                >
                  {tempFilterBrand.length > 0 ? tempFilterBrand[0] : 'Brand'}
                </button>
                <ChevronRight className="text-neutral-300" size={16} />
                <button 
                  onClick={() => tempFilterBrand.length > 0 && setModalStep(2)}
                  disabled={tempFilterBrand.length === 0}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    modalStep === 2
                      ? 'bg-primary-500 text-white font-medium'
                      : tempFilterCategory.length > 0
                        ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        : 'bg-neutral-100 text-neutral-400'
                  }`}
                >
                  {tempFilterCategory.length > 0 ? tempFilterCategory[0] : 'Category'}
                </button>
                <ChevronRight className="text-neutral-300" size={16} />
                <button 
                  onClick={() => tempFilterCategory.length > 0 && setModalStep(3)}
                  disabled={tempFilterCategory.length === 0}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    modalStep === 3
                      ? 'bg-primary-500 text-white font-medium'
                      : tempFilterSize.length > 0
                        ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        : 'bg-neutral-100 text-neutral-400'
                  }`}
                >
                  {tempFilterSize.length > 0 ? tempFilterSize[0] : 'Size'}
                </button>
              </div>

              {/* Step 1: Brand Selection */}
              {modalStep === 1 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                      Step 1: Select Brand{tempFilterBrand.length > 0 && ` (${tempFilterBrand.length})`}
                    </h3>
                    {tempFilterBrand.length > 0 && (
                      <button
                        onClick={() => { 
                          setTempFilterBrand([]);
                        }}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Brand Search Input or Pills - Using server-side suggestions */}
                  {(() => {
                    // Use server-side suggestions for brands (full catalog access)
                    const brandsList = serverBrands || [];
                    const brandCount = brandsList.length;

                    // Only show full loading screen on initial load (no brands yet)
                    // During typing, keep existing results visible to prevent flicker
                    if (brandsLoading && brandCount === 0 && !brandSearchInput) {
                      return (
                        <div className="text-center py-8 text-neutral-400 text-sm">
                          Loading brands...
                        </div>
                      );
                    }

                    if (brandsError) {
                      return (
                        <div className="text-center py-8">
                          <p className="text-error-500 text-sm mb-2">Failed to load brands</p>
                          <button
                            onClick={refetchBrands}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            Retry
                          </button>
                        </div>
                      );
                    }

                    // Only show "no matches" when not loading - prevents flash during typing
                    if (brandCount === 0 && brandSearchInput && !brandsLoading) {
                      return (
                        <div className="text-center py-8">
                          <p className="text-neutral-400 text-sm mb-3">No brands match "{brandSearchInput}"</p>
                          <button
                            onClick={() => {
                              setBrandSearchInput('');
                              setShowBrandDropdown(false);
                            }}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            Clear search
                          </button>
                        </div>
                      );
                    }

                    if (brandCount <= 9 && !brandSearchInput) {
                      return (
                        <div className="grid grid-cols-3 gap-3">
                          {brandsList.map(brand => (
                            <button
                              key={brand}
                              onClick={() => {
                                setTempFilterBrand([brand]);
                                setModalStep(2);
                              }}
                              className={`px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                                tempFilterBrand.includes(brand)
                                  ? 'bg-primary-500 text-white border-primary-500'
                                  : 'bg-white border-neutral-200 hover:border-primary-300 hover:bg-primary-50 text-neutral-700'
                              }`}
                            >
                              {brand}
                            </button>
                          ))}
                        </div>
                      );
                    }

                    return (
                    // Searchable Input for >9 brands or when user is typing
                    <div className="relative">
                      <input
                        id="modal-brand-input"
                        type="text"
                        placeholder="Type brand name..."
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={brandSearchInput}
                        onChange={(e) => {
                          setBrandSearchInput(e.target.value);
                          setShowBrandDropdown(true);
                          setSelectedBrandIndex(-1);
                        }}
                        onFocus={() => setShowBrandDropdown(true)}
                        onKeyDown={(e) => {
                          // Use server suggestions for keyboard nav
                          const matchCount = brandsList.length;

                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setSelectedBrandIndex(prev =>
                              prev < matchCount - 1 ? prev + 1 : 0
                            );
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setSelectedBrandIndex(prev =>
                              prev > 0 ? prev - 1 : matchCount - 1
                            );
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            if (selectedBrandIndex >= 0 && selectedBrandIndex < matchCount) {
                              const selectedBrand = brandsList[selectedBrandIndex];
                              setTempFilterBrand([selectedBrand]);
                              setBrandSearchInput('');
                              setShowBrandDropdown(false);
                              setSelectedBrandIndex(-1);
                              setModalStep(2);
                            }
                          }
                        }}
                      />
                      {showBrandDropdown && (brandSearchInput || brandCount > 9) && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                          {brandsLoading ? (
                            <div className="px-4 py-2 text-neutral-400 text-sm">Searching...</div>
                          ) : brandsList.length > 0 ? (
                            brandsList.map((brand, index) => (
                              <button
                                key={brand}
                                onClick={() => {
                                  setTempFilterBrand([brand]);
                                  setBrandSearchInput('');
                                  setShowBrandDropdown(false);
                                  setSelectedBrandIndex(-1);
                                  setModalStep(2);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                  index === selectedBrandIndex
                                    ? 'bg-primary-500 text-white'
                                    : 'hover:bg-primary-50 text-neutral-700'
                                }`}
                              >
                                {brand}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-neutral-400 text-sm">No matches found</div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {/* "All Brands" Option - Moved to end */}
                  <button
                    onClick={() => {
                      setTempFilterBrand([]);
                      setTempFilterCategory([]);
                      setTempFilterSize([]);
                      setModalStep(2);
                    }}
                    className={`w-full px-4 py-3 rounded-lg border text-left font-medium text-sm transition-colors ${
                      tempFilterBrand.length === 0
                        ? 'bg-primary-500 text-white border-primary-500' 
                        : 'bg-white border-neutral-200 hover:border-primary-300 hover:bg-primary-50 text-neutral-700'
                    }`}
                  >
                    All Brands
                  </button>
                </div>
              )}

              {/* Step 2: Category Selection */}
              {modalStep === 2 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                      Step 2: Select Category{tempFilterCategory.length > 0 && ` (${tempFilterCategory.length})`}
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { 
                          setModalStep(1); 
                        }}
                        className="text-neutral-600 hover:text-neutral-800 text-sm"
                      >
                        ← Back
                      </button>
                      {tempFilterCategory.length > 0 && (
                        <button
                          onClick={() => { 
                            setTempFilterCategory([]);
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Category Selection - Using server-side suggestions */}
                  {(() => {
                    const categoriesList = serverCategories || [];
                    const categoryCount = categoriesList.length;

                    if (categoriesLoading) {
                      return (
                        <div className="text-center py-8 text-neutral-400 text-sm">
                          Loading categories...
                        </div>
                      );
                    }

                    if (categoriesError) {
                      return (
                        <div className="text-center py-8">
                          <p className="text-error-500 text-sm mb-2">Failed to load categories</p>
                          <button
                            onClick={refetchCategories}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            Retry
                          </button>
                        </div>
                      );
                    }

                    if (categoryCount === 0) {
                      return (
                        <div className="text-center py-8 text-neutral-400 text-sm">
                          No categories available
                        </div>
                      );
                    }

                    if (categoryCount <= 9) {
                      return (
                        <div className="grid grid-cols-3 gap-3">
                          {categoriesList.map(category => (
                            <button
                              key={category}
                              onClick={() => {
                                setTempFilterCategory([category]);
                                setModalStep(3);
                              }}
                              className={`px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                                tempFilterCategory.includes(category)
                                  ? 'bg-primary-500 text-white border-primary-500'
                                  : 'bg-white border-neutral-200 hover:border-primary-300 hover:bg-primary-50 text-neutral-700'
                              }`}
                            >
                              {category}
                            </button>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <div className="relative">
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              setTempFilterCategory([e.target.value]);
                              setModalStep(3);
                            }
                          }}
                          value={tempFilterCategory.length > 0 ? tempFilterCategory[0] : ''}
                          className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white"
                        >
                          <option value="">
                            Select from {categoryCount} categories...
                          </option>
                          {categoriesList.map(category => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" size={20} />
                      </div>
                    );
                  })()}

                  {/* "All Categories" Option - Moved to end */}
                  <button
                    onClick={() => {
                      setTempFilterCategory([]);
                      setTempFilterSize([]);
                      setModalStep(3);
                    }}
                    className={`w-full px-4 py-3 rounded-lg border text-left font-medium text-sm transition-colors ${
                      tempFilterCategory.length === 0
                        ? 'bg-primary-500 text-white border-primary-500' 
                        : 'bg-white border-neutral-200 hover:border-primary-300 hover:bg-primary-50 text-neutral-700'
                    }`}
                  >
                    All Categories
                  </button>
                </div>
              )}

              {/* Step 3: Size Selection */}
              {modalStep === 3 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                      Step 3: Select Size{tempFilterSize.length > 0 && ` (${tempFilterSize.length})`}
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { 
                          setModalStep(2);
                        }}
                        className="text-neutral-600 hover:text-neutral-800 text-sm"
                      >
                        ← Back
                      </button>
                      {tempFilterSize.length > 0 && (
                        <button
                          onClick={() => setTempFilterSize([])}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Size Selection - Using server-side suggestions */}
                  {(() => {
                    const sizesList = serverSizes || [];
                    const sizeCount = sizesList.length;

                    if (sizesLoading) {
                      return (
                        <div className="text-center py-8 text-neutral-400 text-sm">
                          Loading sizes...
                        </div>
                      );
                    }

                    if (sizesError) {
                      return (
                        <div className="text-center py-8">
                          <p className="text-error-500 text-sm mb-2">Failed to load sizes</p>
                          <button
                            onClick={refetchSizes}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            Retry
                          </button>
                        </div>
                      );
                    }

                    if (sizeCount === 0) {
                      return (
                        <div className="text-center py-8 text-neutral-400 text-sm">
                          No sizes available
                        </div>
                      );
                    }

                    if (sizeCount <= 9) {
                      return (
                        <div className="grid grid-cols-3 gap-3">
                          {sizesList.map(size => (
                            <button
                              key={size}
                              onClick={() => applyModalFiltersAndClose([size])}
                              className={`px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                                tempFilterSize.includes(size)
                                  ? 'bg-primary-500 text-white border-primary-500'
                                  : 'bg-white border-neutral-200 hover:border-primary-300 hover:bg-primary-50 text-neutral-700'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <div className="relative">
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              applyModalFiltersAndClose([e.target.value]);
                            }
                          }}
                          value={tempFilterSize.length > 0 ? tempFilterSize[0] : ''}
                          className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white"
                        >
                          <option value="">
                            Select from {sizeCount} sizes...
                          </option>
                          {sizesList.map(size => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" size={20} />
                      </div>
                    );
                  })()}

                  {/* "All Sizes" Option - Moved to end */}
                  <button
                    onClick={() => applyModalFiltersAndClose([])}
                    className={`w-full px-4 py-3 rounded-lg border text-left font-medium text-sm transition-colors ${
                      tempFilterSize.length === 0
                        ? 'bg-primary-500 text-white border-primary-500' 
                        : 'bg-white border-neutral-200 hover:border-primary-300 hover:bg-primary-50 text-neutral-700'
                    }`}
                  >
                    All Sizes
                  </button>
                </div>
              )}
            </div>

            {/* Main Search Input - Moved to bottom */}
            <div className="px-6 pb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by UPC, brand, description, category..."
                  className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setIsUpcSearchMode(false);
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-neutral-200 p-6 bg-neutral-50">
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-2xl font-bold text-neutral-800">
                    {previewLoading ? '...' : (previewCount ?? catalogStats.total).toLocaleString()}
                  </p>
                  <p className="text-sm text-neutral-500">products found</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setTempFilterBrand([]);
                      setTempFilterCategory([]);
                      setTempFilterSize([]);
                      setSearchInput('');
                      setModalStep(1);
                      setSelectedBrandIndex(-1);
                    }}
                    className="px-6 py-2.5 border border-neutral-300 bg-white rounded-lg text-sm font-semibold hover:bg-neutral-50 transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => applyModalFiltersAndClose(tempFilterSize)}
                    className="px-6 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spot Clipboard Modal */}
      {showSpotClipboardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-neutral-800">Paste and Search</h3>
                <button
                  onClick={() => {
                    setShowSpotClipboardModal(false);
                    setSpotClipboardText('');
                  }}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 text-center space-y-4">
              <p className="text-neutral-600 text-sm">
                Click the button below to automatically paste from clipboard and search for products.
              </p>
              <p className="text-xs text-neutral-500">
                Make sure you have copied the spot information to your clipboard.
              </p>
            </div>
            <div className="p-6 border-t border-neutral-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSpotClipboardModal(false);
                  setSpotClipboardText('');
                }}
                className="px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAutoPasteSpot}
                className="px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center gap-2"
              >
                <Check size={16} />
                Paste Spot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {commentModalProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-neutral-800 flex items-center gap-2">
                  <MessageSquare className="text-warning-500" size={24} />
                  Product Comment
                </h3>
                <button
                  onClick={() => {
                    if (isSavingComment) return;
                    setCommentModalProduct(null);
                    setProductComment('');
                    setCommentError(null);
                  }}
                  disabled={isSavingComment}
                  className="text-neutral-400 hover:text-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mt-2 text-sm text-neutral-600">
                <strong>{commentModalProduct.brand}</strong> - {commentModalProduct.description}
              </div>
            </div>
            <div className="p-6">
              {commentError && (
                <div className="mb-4 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm font-medium text-error-700">
                  {commentError}
                </div>
              )}
              <textarea
                value={productComment}
                onChange={(e) => setProductComment(e.target.value)}
                disabled={isSavingComment}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-warning-500 resize-none disabled:opacity-60"
                rows={6}
                placeholder="Add your comments about this product..."
              />
            </div>
            <div className="p-6 border-t border-neutral-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  if (isSavingComment) return;
                  setCommentModalProduct(null);
                  setProductComment('');
                  setCommentError(null);
                }}
                disabled={isSavingComment}
                className="px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={saveComment}
                disabled={isSavingComment}
                className="px-4 py-2 bg-warning-600 text-white rounded-lg text-sm font-semibold hover:bg-warning-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save size={16} />
                {isSavingComment ? 'Saving...' : 'Save Comment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-enable Product Confirmation Modal */}
      {confirmReEnableProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-neutral-200">
              <h3 className="text-lg font-bold text-neutral-800">Re-enable Product?</h3>
            </div>
            <div className="p-6">
              <p className="text-neutral-600 mb-4">
                Do you want to re-enable this product for copying?
              </p>
              <div className="bg-neutral-50 p-3 rounded-lg text-sm">
                <div className="font-semibold text-neutral-700">{confirmReEnableProduct.brand}</div>
                <div className="text-neutral-600">{confirmReEnableProduct.description}</div>
                <div className="text-neutral-500 text-xs mt-1">UPC: {confirmReEnableProduct.upc}</div>
              </div>
            </div>
            <div className="p-6 border-t border-neutral-200 flex justify-end gap-3">
              <button
                onClick={() => setConfirmReEnableProduct(null)}
                className="px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setDisabledProducts(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(confirmReEnableProduct.upc);
                    return newSet;
                  });
                  setHistoryUsedUpcs(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(confirmReEnableProduct.upc);
                    return newSet;
                  });
                  setConfirmReEnableProduct(null);
                }}
                className="px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Re-enable
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Product Form Modal */}
      {showNewProductForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-neutral-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-neutral-800 flex items-center gap-2">
                  <Plus className="text-success-500" size={24} />
                  Create New Product
                </h3>
                <button
                  onClick={() => {
                    if (isSavingNewProduct) return;
                    setShowNewProductForm(false);
                    setNewProduct({});
                  }}
                  disabled={isSavingNewProduct}
                  className="text-neutral-400 hover:text-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {newProductError && (
                <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
                  <p className="text-sm font-medium text-error-700">{newProductError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Spot Link
                  </label>
                  <input
                    type="text"
                    value={newProduct.spotNumberLink || ''}
                    onChange={(e) => handleNewProductChange('spotNumberLink', e.target.value)}
                    onBlur={handleSpotLinkBlur}
                    placeholder="Paste the spot link"
                    aria-invalid={Boolean(spotLinkError)}
                    aria-describedby="spot-link-hint"
                    className={`w-full px-3 py-2 border rounded text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 ${
                      spotLinkError
                        ? 'border-error-400 focus:ring-error-500'
                        : 'border-neutral-200 focus:ring-success-500'
                    }`}
                  />
                  {/* One line under the field at a time: the format while the
                      value is fine, the complaint once it isn't. Mirrors the
                      `error || helperText` rule in the shared Input component. */}
                  <p
                    id="spot-link-hint"
                    className={`mt-1 text-xs ${spotLinkError ? 'text-error-600' : 'text-neutral-400'}`}
                  >
                    {spotLinkError || SPOT_LINK_FORMAT_HINT}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Suggested Description
                  </label>
                  <input
                    type="text"
                    value={newProduct.suggestedDescription || ''}
                    onChange={(e) => handleNewProductChange('suggestedDescription', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Reference Link
                  </label>
                  <input
                    type="text"
                    value={newProduct.referenceLink || ''}
                    onChange={(e) => handleNewProductChange('referenceLink', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-500 uppercase mb-1">
                    Variety Type <span className="text-error-500">*</span>
                  </label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="varietyType"
                        value="New Variety"
                        checked={newProduct.varietyType === 'New Variety'}
                        onChange={(e) => handleNewProductChange('varietyType', e.target.value)}
                        className="w-4 h-4 text-success-600 focus:ring-success-500"
                      />
                      <span className="text-sm font-medium text-neutral-700">New Variety</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="varietyType"
                        value="Extend Existing Variety"
                        checked={newProduct.varietyType === 'Extend Existing Variety'}
                        onChange={(e) => handleNewProductChange('varietyType', e.target.value)}
                        className="w-4 h-4 text-success-600 focus:ring-success-500"
                      />
                      <span className="text-sm font-medium text-neutral-700">Extend Existing Variety</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-neutral-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  if (isSavingNewProduct) return;
                  setShowNewProductForm(false);
                  setNewProduct({});
                }}
                disabled={isSavingNewProduct}
                className="px-4 py-2 text-sm font-semibold text-neutral-600 hover:text-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={saveNewProduct}
                disabled={isSavingNewProduct}
                className="px-4 py-2 bg-success-600 text-white rounded-lg text-sm font-semibold hover:bg-success-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save size={16} />
                {isSavingNewProduct ? 'Creating...' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Product History — native spot participation grid (replaces Tableau) */}
      {historyProduct && (
        <ProductSpotHistoryModal
          product={historyProduct}
          initialRetailer={filterRetailer}
          onClose={() => setHistoryProduct(null)}
          usedUpcs={historyUsedUpcs}
          onCopyUpc={(upc) => {
            copyUPCToClipboard(upc);
            setHistoryUsedUpcs(prev => new Set(prev).add(upc));
          }}
          onRequestReEnable={(row) => setConfirmReEnableProduct(row)}
          suspendEscape={Boolean(confirmReEnableProduct)}
        />
      )}
    </div>
  );
};

export default App;
